import { AccountWithPrivateKey } from '@vertex-protocol/client';
import { useEVMContext } from '@vertex-protocol/react-client';
import { WithChildren } from '@vertex-protocol/web-common';
import { SUBACCOUNT_LIMIT } from 'client/context/subaccount/consts';
import { useSubaccountNames } from 'client/context/subaccount/hooks/useSubaccountNames';
import {
  SubaccountContext,
  SubaccountContextData,
} from 'client/context/subaccount/SubaccountContext';
import {
  AppSubaccount,
  UpdateSigningPreferenceFn,
} from 'client/context/subaccount/types';
import { getSigningPreferenceToSave } from 'client/context/subaccount/getSigningPreferenceToSave';
import { LinkedSignerSync } from 'client/modules/singleSignatureSessions/components/LinkedSignerSync';
import { useSavedSubaccountSigningPreference } from 'client/modules/singleSignatureSessions/hooks/useSavedSubaccountSigningPreference';
import { SubaccountSigningPreference } from 'client/modules/singleSignatureSessions/types';
import { PRIMARY_SUBACCOUNT_NAME } from 'client/modules/subaccounts/consts';
import { useSavedSubaccountSettings } from 'client/modules/subaccounts/hooks/useSavedSubaccountSettings';
import { getDefaultSubaccountProfile } from 'client/modules/subaccounts/utils/getDefaultSubaccountProfile';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Context for managing the current subaccount and its signing preferences
 */
export function SubaccountContextProvider({ children }: WithChildren) {
  const {
    connectionStatus,
    primaryChain: { id: primaryChainId },
    primaryChainEnv,
  } = useEVMContext();

  /**
   * Subaccount
   */

  const {
    selectedSubaccountName,
    saveSelectedSubaccountName,
    getSavedSubaccountProfile,
  } = useSavedSubaccountSettings();

  const currentSubaccount = useMemo((): AppSubaccount => {
    return {
      name: selectedSubaccountName ?? PRIMARY_SUBACCOUNT_NAME,
      chainEnv: primaryChainEnv,
      chainId: primaryChainId,
      address: connectionStatus?.address,
    };
  }, [
    selectedSubaccountName,
    primaryChainEnv,
    primaryChainId,
    connectionStatus?.address,
  ]);

  const currentSubaccountProfile = useMemo(
    () =>
      getSavedSubaccountProfile(currentSubaccount.name) ??
      getDefaultSubaccountProfile(currentSubaccount.name),
    [currentSubaccount.name, getSavedSubaccountProfile],
  );

  const getSubaccountProfile = useCallback(
    (subaccountName: string) =>
      getSavedSubaccountProfile(subaccountName) ??
      getDefaultSubaccountProfile(subaccountName),
    [getSavedSubaccountProfile],
  );

  const { app: appSubaccountNames } = useSubaccountNames();

  const canAddSubaccount = appSubaccountNames.length < SUBACCOUNT_LIMIT;

  /**
   * Signing preferences
   */

  // Authorized linked signer for the current session - not persisted
  const [linkedSignerForSession, setLinkedSignerForSession] =
    useState<AccountWithPrivateKey>();

  // Clear session linked signer on subaccount changes.
  // Depend on identity values, not the object reference, to avoid
  // clearing the signer when the memo returns a new object with
  // identical contents (e.g. during wallet interactions).
  useEffect(() => {
    setLinkedSignerForSession(undefined);
  }, [currentSubaccount.address, currentSubaccount.name, currentSubaccount.chainEnv]);

  const {
    signingPreference: savedSigningPreference,
    didLoadPersistedValue: didLoadSigningPreferencePersistedValue,
    saveSigningPreference,
  } = useSavedSubaccountSigningPreference(currentSubaccount.name);

  // Consolidate signing preference with any session state
  const signingPreference = useMemo(():
    | SubaccountSigningPreference
    | undefined => {
    if (!savedSigningPreference) {
      return;
    }
    if (savedSigningPreference.type === 'sign_always') {
      return savedSigningPreference;
    }
    return {
      ...savedSigningPreference,
      linkedSigner:
        savedSigningPreference.linkedSigner ?? linkedSignerForSession,
    };
  }, [savedSigningPreference, linkedSignerForSession]);

  // Update fn for signing preference changes
  const updateSigningPreference = useCallback<UpdateSigningPreferenceFn>(
    (newValue) => {
      saveSigningPreference(getSigningPreferenceToSave(newValue));

      if (newValue.type === 'sign_always') {
        setLinkedSignerForSession(undefined);
      } else {
        setLinkedSignerForSession(newValue.linkedSigner);
      }
    },
    [saveSigningPreference],
  );

  const data: SubaccountContextData = useMemo(() => {
    return {
      currentSubaccount,
      setCurrentSubaccountName: saveSelectedSubaccountName,
      currentSubaccountProfile,
      getSubaccountProfile,
      appSubaccountNames,
      canAddSubaccount,
      signingPreference: {
        current: signingPreference,
        didLoadPersistedValue: didLoadSigningPreferencePersistedValue,
        update: updateSigningPreference,
      },
    };
  }, [
    currentSubaccount,
    saveSelectedSubaccountName,
    currentSubaccountProfile,
    getSubaccountProfile,
    appSubaccountNames,
    canAddSubaccount,
    signingPreference,
    didLoadSigningPreferencePersistedValue,
    updateSigningPreference,
  ]);

  return (
    <SubaccountContext.Provider value={data}>
      <LinkedSignerSync />
      {children}
    </SubaccountContext.Provider>
  );
}
